import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { store } from '../..';
import { StatsCharacterMapping } from '@/models/statistics/stats-character-mapping';
import { StatsNetwork, StatsNetworkCategories, StatsNetworkLinks, StatsNetworkNodes } from '@/models/statistics/stats-network';
import { SchoolGroupPMAnalysis } from '@/models/school-group/school-group-pm-analysis';
import { GEnum } from '@/constants/enum.defination';
import { schoolGroupPMAnalysisService } from '@/service/api/school-group-pm-analysis.service';
import { SchoolGroupPMAnalysisParams } from '@/models/school-group/school-group-pm-analysis-params';
import { StatisticsFilterParam } from '@/models/statistics/statistics-filter-param';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { statsService } from '@/service/api/stats.service';
import { Competency } from '@/models/competency/competency';
import { EChartService } from '@/service/echart/echart.service';
import { CompetencyBoxplotEchartService } from '@/service/echart/competency-boxplot.echart.service';
import { SurveyAnalysisMapEchartService } from '@/service/echart/survey-analysis-map.echart.service';
import ChartType = GEnum.ChartType;
import TrendAnalysisChartType = GEnum.TrendAnalysisChartType;
import { StatsRequest } from '@/models/statistics/stats-request';
import { SchoolGroupTrendAnalysis } from '@/models/school-group/school-group-trend-analysis';
import { SchoolGroupTrendAnalysisParams } from '@/models/school-group/school-group-trend-analysis-params';
import { schoolGroupTrendAnalysisService } from '@/service/api/school-group-trend-analysis.service';
import { TrendanalysisGrowthMindsetMapEchartService } from '@/service/echart/trendanalysis-growth-mindset-map.echart.service';
import { SurveyAnalysisCircleEchartService } from '@/service/echart/survey-analysis-circle-echart-service';

export interface IStatisticsState {
  schoolGroupPMAnalysisLeaderShip: SchoolGroupPMAnalysis; // リーダシップ別PMデータ
  schoolGroupPMAnalysisInnovation: SchoolGroupPMAnalysis; // イノベーション別PMデータ
  schoolGroupPMAnalysisCriticalThinking: SchoolGroupPMAnalysis; // 批評的思考力別PMデータ
  schoolGroupPMAnalysisCreativeThinking: SchoolGroupPMAnalysis; // 創造的思考力別PMデータ
  schoolGroupPMAnalysisCollaborationThinking: SchoolGroupPMAnalysis; // 協働的思考力PMデータ
  currentGrowingPurposeID: number; // 現在選択している成長目的ID
  schoolGroupTrendAnalysis: SchoolGroupTrendAnalysis; // 傾向チェックデータ

  statisticsFilterParam: StatisticsFilterParam; // 統計検索条件
  statsCompetencyBoxplots: StatsCompetencyBoxplot[][]; // コンピテンシー分布
  statsCharacterMappings: StatsCharacterMapping[]; // 気質診断データ
  statsNetworks: StatsNetwork[]; // ネットワークデータ
  echartService: EChartService; // echartを制御するサービス
  trendAnalysisEchartService: EChartService; // echartを制御するサービス(trendanalysis)
  surveyAnalysisCircleEchartService: EChartService; // echartを制御するサービス(survey 円グラフ用)
  competencies: Competency[]; // コンピテンシー一覧(コンピテンシー分布のラベル表示に使用)
  selectedChartType: GEnum.ChartType; // 現在タブ表示しているチャートタイプ
  statisticsFilterParams: StatisticsFilterParam[]; // 選択中の系列一覧
  loading: boolean; // チャートデータ取得中
  loadingTrendAnalysis: boolean; // チャートデータ取得中
  selectedTrendAnalysisChartType: GEnum.TrendAnalysisChartType; // 現在タブ表示しているチャートタイプ（傾向チェック）
  selectedSurveyAnalysisChartType: GEnum.SurveyAnalysisChartType; // 現在タブ表示しているチャートタイプ（サーベイ分析）
  statsTrendAnalysis: SchoolGroupTrendAnalysis[]; // 傾向チェック用データ
}
@Module({ dynamic: true, store, name: 'statisticsModule', namespaced: true })
class StatisticsModule extends VuexModule implements IStatisticsState {
  schoolGroupPMAnalysisLeaderShip: SchoolGroupPMAnalysis = undefined;
  schoolGroupPMAnalysisInnovation: SchoolGroupPMAnalysis = undefined;
  schoolGroupPMAnalysisCriticalThinking: SchoolGroupPMAnalysis = undefined;
  schoolGroupPMAnalysisCreativeThinking: SchoolGroupPMAnalysis = undefined;
  schoolGroupPMAnalysisCollaborationThinking: SchoolGroupPMAnalysis = undefined;
  currentGrowingPurposeID: number = 0;
  schoolGroupTrendAnalysis: SchoolGroupTrendAnalysis = new SchoolGroupTrendAnalysis();

  statisticsFilterParam: StatisticsFilterParam = new StatisticsFilterParam();
  statsCompetencyBoxplots: StatsCompetencyBoxplot[][] = [];
  statsCharacterMappings: StatsCharacterMapping[] = [];
  statsNetworks: StatsNetwork[] = [];
  echartService: EChartService = new CompetencyBoxplotEchartService();
  trendAnalysisEchartService: EChartService = new TrendanalysisGrowthMindsetMapEchartService();
  surveyAnalysisCircleEchartService: EChartService = new SurveyAnalysisCircleEchartService();
  competencies: Competency[] = [];
  selectedChartType: ChartType = GEnum.ChartType.CompetencyBoxplot;
  statisticsFilterParams: StatisticsFilterParam[] = [];
  loading: boolean = false;
  loadingTrendAnalysis: boolean = false;
  selectedTrendAnalysisChartType: TrendAnalysisChartType = GEnum.TrendAnalysisChartType.GrowthMindsetMap;
  selectedSurveyAnalysisChartType: GEnum.SurveyAnalysisChartType = GEnum.SurveyAnalysisChartType.AnswerBarGraph;
  statsTrendAnalysis: SchoolGroupTrendAnalysis[] = [];

  get competencyByType(): Competency[] {
    return this.competencies.sort(function (a, b): number {
      return a.competencyType > b.competencyType ? 1 : -1;
    });
  }

  get schoolGroupPMAnalysisByGrowingPurposeID(): SchoolGroupPMAnalysis {
    switch (this.currentGrowingPurposeID) {
      case GEnum.GrowingPurpose.LeaderShip:
        return this.schoolGroupPMAnalysisLeaderShip;
      case GEnum.GrowingPurpose.Innovation:
        return this.schoolGroupPMAnalysisInnovation;
      case GEnum.GrowingPurpose.CriticalThinking:
        return this.schoolGroupPMAnalysisCriticalThinking;
      case GEnum.GrowingPurpose.CreativeThinking:
        return this.schoolGroupPMAnalysisCreativeThinking;
      case GEnum.GrowingPurpose.CollaborationThinking:
        return this.schoolGroupPMAnalysisCollaborationThinking;
      default:
    }
  }

  // チャートタイプ別のseriesで使用するデータを取得する
  get chartData(): any {
    switch (this.selectedChartType) {
      case GEnum.ChartType.CompetencyBoxplot:
        return statisticsModule.statsCompetencyBoxplots;
      case GEnum.ChartType.OtherCompetencyBoxplot:
        return statisticsModule.statsCompetencyBoxplots;
      case GEnum.ChartType.CustomCompetencyBoxplot:
        return statisticsModule.statsCompetencyBoxplots;
      case GEnum.ChartType.CharacterMapping:
        return statisticsModule.statsCharacterMappings;
      case GEnum.ChartType.MVPlotCompetency:
        return statisticsModule.statsCompetencyBoxplots;
      case GEnum.ChartType.MVPlotStudent:
        return statisticsModule.statsCompetencyBoxplots;
      case GEnum.ChartType.CharacterBoxplot:
        return statisticsModule.statsCharacterMappings;
      case GEnum.ChartType.Network:
        return statisticsModule.selectedNetworkData;
      case GEnum.ChartType.CompetencyGrowthChart:
        return statisticsModule.statsCompetencyBoxplots;
    }
  }

  // チャートタイプ別のseriesで使用するデータを取得する
  get chartDataTrendAnalysis(): any {
    return statisticsModule.statsTrendAnalysis;
  }

  // 一つに集約したネットワークを取得する
  get selectedNetworkData(): StatsNetwork {
    return this.statsNetworks.reduce((result: StatsNetwork, network: StatsNetwork) => {
      let uniqueCategory: Set<string> = new Set<string>();
      let uniqueNode: Set<string> = new Set<string>();
      let uniqueLink: Set<string> = new Set<string>();

      result.categories = result.categories.concat(network.categories).filter((category: StatsNetworkCategories) => {
        if (uniqueCategory.has(category.name)) {
          return false;
        }
        uniqueCategory.add(category.name);
        return true;
      });

      result.nodes = result.nodes.concat(network.nodes).filter((node: StatsNetworkNodes) => {
        if (uniqueNode.has(node.name)) {
          return false;
        }
        uniqueNode.add(node.name);
        return true;
      });
      result.links = result.links.concat(network.links).filter((link: StatsNetworkLinks) => {
        const key: string = `${link.source}-${link.target}`;
        if (uniqueLink.has(key)) {
          return false;
        }
        uniqueLink.add(key);
        return true;
      });

      return result;
    }, new StatsNetwork());
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setLoadingTrendAnalysis(value: boolean) {
    this.loadingTrendAnalysis = value;
  }

  @Mutation
  addStatisticsFilterParams(statisticsFilterParam: StatisticsFilterParam) {
    this.statisticsFilterParams.push(statisticsFilterParam);
  }

  @Mutation
  removeStatisticsFilterParams(index: number) {
    this.statisticsFilterParams.splice(index, 1);
  }

  @Mutation
  setSelectedChartType(chartType: GEnum.ChartType) {
    this.selectedChartType = chartType;
  }

  @Mutation
  setSelectedTrendAnalysisChartType(chartType: GEnum.TrendAnalysisChartType) {
    this.selectedTrendAnalysisChartType = chartType;
  }

  @Mutation
  setSelectedSurveyAnalysisChartType(chartType: GEnum.SurveyAnalysisChartType) {
    this.selectedSurveyAnalysisChartType = chartType;
  }

  @Mutation
  setCompetencies(value: Competency[]) {
    this.competencies = value;
  }

  @Mutation
  setEChartService(echartService: EChartService) {
    this.echartService = echartService;
  }

  @Mutation
  setTrendAnalysisEChartService(echartService: EChartService) {
    this.trendAnalysisEchartService = echartService;
  }

  @Mutation
  setSurveyAnalysisCircleEchartService(echartService: EChartService) {
    this.surveyAnalysisCircleEchartService = echartService;
  }

  @Mutation
  addStatsCompetencyBoxplots(value: StatsCompetencyBoxplot[]) {
    this.statsCompetencyBoxplots.push(value);
  }

  @Mutation
  removeStatsNetworks(index: number) {
    this.statsNetworks.splice(index, 1);
  }

  @Mutation
  addStatsCharacterMappings(value: StatsCharacterMapping) {
    this.statsCharacterMappings.push(value);
  }

  @Mutation
  setStatsNetworks(value: StatsNetwork[]) {
    this.statsNetworks = value;
  }

  @Mutation
  setSchoolGroupPMAnalysisLeaderShip(value: SchoolGroupPMAnalysis) {
    this.schoolGroupPMAnalysisLeaderShip = value;
  }

  @Mutation
  setSchoolGroupPMAnalysisInnovation(value: SchoolGroupPMAnalysis) {
    this.schoolGroupPMAnalysisInnovation = value;
  }

  @Mutation
  setSchoolGroupPMAnalysisCriticalThinking(value: SchoolGroupPMAnalysis) {
    this.schoolGroupPMAnalysisCriticalThinking = value;
  }

  @Mutation
  setSchoolGroupPMAnalysisCreativeThinking(value: SchoolGroupPMAnalysis) {
    this.schoolGroupPMAnalysisCreativeThinking = value;
  }

  @Mutation
  setSchoolGroupPMAnalysisCollaborationThinking(value: SchoolGroupPMAnalysis) {
    this.schoolGroupPMAnalysisCollaborationThinking = value;
  }

  @Mutation
  setCurrentGrowingPurposeID(value: number) {
    this.currentGrowingPurposeID = value;
  }

  @Mutation
  cleanSchoolGroupPMAnalysis() {
    this.schoolGroupPMAnalysisLeaderShip = undefined;
    this.schoolGroupPMAnalysisInnovation = undefined;
    this.schoolGroupPMAnalysisCriticalThinking = undefined;
    this.schoolGroupPMAnalysisCreativeThinking = undefined;
    this.schoolGroupPMAnalysisCollaborationThinking = undefined;
    this.currentGrowingPurposeID = 1;
  }

  @Mutation
  setSchoolGroupTrendAnalysis(value: SchoolGroupTrendAnalysis) {
    this.schoolGroupTrendAnalysis = value;
  }

  @Mutation
  addstatsTrendAnalysis(value: SchoolGroupTrendAnalysis) {
    this.statsTrendAnalysis.push(value);
  }

  @Mutation
  cleanSchoolGroupTrendAnalysis() {
    this.schoolGroupTrendAnalysis = undefined;
  }

  @Mutation
  clean() {
    this.statsCompetencyBoxplots = [];
    this.statsCharacterMappings = [];
    this.statsNetworks = [];
    this.competencies = [];
    this.selectedChartType = GEnum.ChartType.CompetencyBoxplot;
    this.selectedTrendAnalysisChartType = GEnum.TrendAnalysisChartType.GrowthMindsetMap;
    this.selectedSurveyAnalysisChartType = GEnum.SurveyAnalysisChartType.AnswerBarGraph;
    this.statisticsFilterParams = [];
    this.schoolGroupTrendAnalysis = undefined;
    this.statsTrendAnalysis = [];
  }

  @Mutation
  setStatisticsFilterParam(value: StatisticsFilterParam) {
    this.statisticsFilterParam = value;
  }

  /**
   * コンピテンシー分布取得
   * @param request 統計情報の検索条件パラメータ
   */
  @Action({ rawError: true })
  async fetchCompetencyDistribution(request: StatsRequest) {
    const result: StatsCompetencyBoxplot[] = await statsService.getCompetencyDistribution(request.targetType, request.targetID, request.selectedTagIDs, request.evalSettingID);
    this.addStatsCompetencyBoxplots(result);
  }

  /**
   * 気質診断結果取得
   * @param request 統計情報の検索条件パラメータ
   */
  @Action({ rawError: true })
  async fetchStatsCharacterMappings(request: StatsRequest) {
    const result = await statsService.getStatsCharacterMapping(request.targetType, request.targetID, request.selectedTagIDs, request.evalSettingID);
    this.addStatsCharacterMappings(result);
  }

  /**
   * ネットワーク分析結果取得
   * @param request 統計情報の検索条件パラメータ
   */
  @Action({ rawError: true })
  async fetchStatsNetworks(request: StatsRequest) {
    let newNetwork: StatsNetwork = await statsService.getStatsNetwork(request.targetType, request.targetID, request.selectedTagIDs, request.evalSettingID);
    this.statsNetworks.push(newNetwork);
  }

  /**
   * PM分析結果取得
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchSchoolGroupPMAnalysis(params: SchoolGroupPMAnalysisParams) {
    const result = await schoolGroupPMAnalysisService.getSchoolGroupPMAnalysis(params.targetType, params.targetID, params.growingPurpose, params.selectedCommaTagIDs);
    if (result) {
      switch (params.growingPurpose) {
        case GEnum.GrowingPurpose.LeaderShip:
          this.setSchoolGroupPMAnalysisLeaderShip(result);
          break;
        case GEnum.GrowingPurpose.Innovation:
          this.setSchoolGroupPMAnalysisInnovation(result);
          break;
        case GEnum.GrowingPurpose.CriticalThinking:
          this.setSchoolGroupPMAnalysisCriticalThinking(result);
          break;
        case GEnum.GrowingPurpose.CreativeThinking:
          this.setSchoolGroupPMAnalysisCreativeThinking(result);
          break;
        case GEnum.GrowingPurpose.CollaborationThinking:
          this.setSchoolGroupPMAnalysisCollaborationThinking(result);
          break;
        default:
      }
    }
  }

  /**
   * 傾向チェック分析結果取得
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchSchoolGroupTrendAnalysis(params: SchoolGroupTrendAnalysisParams) {
    const result = await schoolGroupTrendAnalysisService.getSchoolGroupTrendAnalysis(params.targetType, params.targetID, params.selectedCommaTagIDs);
    this.setSchoolGroupTrendAnalysis(result);
  }

  /**
   * 傾向チェック分析結果取得 - 統計情報ページ用
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchSchoolGroupTrendAnalysisForStatistics(params: StatsRequest) {
    const result = await schoolGroupTrendAnalysisService.getSchoolGroupTrendAnalysisForStatistics(params.targetType, params.targetID, params.selectedTagIDs, params.evalSettingID);
    this.addstatsTrendAnalysis(result);
  }
}

export const statisticsModule = getModule(StatisticsModule);
